import React, { useState } from 'react';
import { Download } from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, BarChart, Bar,
} from 'recharts';
import { mockStats } from '@/data/adminMockData';
import { useReportsDashboard } from '@/hooks';

const AMBER = '#F59E0B';
const GRAY = '#D1D5DB';
const BAR_COLORS = [AMBER, '#9CA3AF', '#6B7280', '#4B5563', '#374151'];
const CHART_STYLE = {
  background: '#fff',
  border: '1px solid #E8E4DC',
  borderRadius: 8,
  fontSize: 12,
  color: '#6B7280',
};

const ReportsModule: React.FC = () => {
  const [dateRange, setDateRange] = useState<'7D' | '30D' | '90D'>('30D');

  // Fetch live dashboard data
  const { data: liveStats, isLoading, isError } = useReportsDashboard();

  // Decide which dataset to use
  const stats = (!isLoading && !isError && liveStats) ? liveStats : mockStats;

  // Normalize overview to guarantee extended KPI fields (fallback to 0 when absent)
  const overview = {
    ...stats.overview,
    averageLoanDurationDays: (stats.overview as any).averageLoanDurationDays ?? 0,
    collectionUtilizationPercentage: (stats.overview as any).collectionUtilizationPercentage ?? 0,
    avgBooksPerUser: (stats.overview as any).avgBooksPerUser ?? 0,
    avgDailyReturns: (stats.overview as any).avgDailyReturns ?? 0,
    returnRatePercentage: (stats.overview as any).returnRatePercentage ?? 0,
    totalLoans: (stats.overview as any).totalLoans ?? (stats.overview as any).activeLoans ?? 0,
    totalReturns: (stats.overview as any).totalReturns ?? (stats.overview as any).totalReturnedLoans ?? 0,
    totalFinesCollected: (stats.overview as any).totalFinesCollected ?? (stats.overview as any).total_fines ?? 0,
  };

  // Derive chart-compatible arrays from the mock/live data structure
  const loanTrends = (stats as any).loanTrends
    ?? (stats as any).loan_trends
    // monthlyTrends uses { month, borrowed, returned }: remap to { date, loans, returns }
    ?? ((stats as any).loans?.monthlyTrends ?? []).map((t: any) => ({
      date: t.month,
      loans: t.borrowed,
      returns: t.returned,
    }));

  const categoryBreakdown = (stats as any).categoryBreakdown
    ?? (stats as any).category_breakdown
    // byCategory uses { category, count }: remap to { name, count }
    ?? ((stats as any).books?.byCategory ?? []).map((c: any) => ({
      name: c.category ?? c.name,
      count: c.count,
    }));

  const userTypeBreakdown = (stats as any).userTypeBreakdown
    ?? (stats as any).user_type_breakdown
    // byRole uses { role, count }: remap to { name, value }
    ?? ((stats as any).users?.byRole ?? []).map((r: any) => ({
      name: r.role ?? r.name,
      value: r.count ?? r.value,
    }));

  // popularBooks: live API may return { title, borrow_count }; mock has { title, borrowCount }
  const popularBooks = (stats as any).popularBooks
    ?? (stats as any).popular_books
    ?? ((stats as any).books?.popular ?? []).map((b: any) => ({
      title: b.title ?? b.book_title,
      count: b.borrow_count ?? b.borrowCount ?? b.count ?? 0,
    }));

  // topBorrowers: live API shape; the mock has no equivalent, so default to empty
  const topBorrowers: any[] = (stats as any).topBorrowers
    ?? (stats as any).top_borrowers
    ?? [];

  return (
    <div className="space-y-5 max-w-[1400px]">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-[22px] font-bold text-[var(--lib-ink)]">Reports</h1>
          <p className="text-sm text-[var(--lib-ink-muted)] mt-1">Analytics and usage statistics.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1">
            {(['7D', '30D', '90D'] as const).map(r => (
              <button
                key={r}
                onClick={() => setDateRange(r)}
                className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors ${
                  dateRange === r ? 'bg-[var(--lib-gold)] text-[var(--lib-on-gold)]' : 'text-[var(--lib-ink-muted)] hover:text-[var(--lib-ink-muted)]'
                }`}
              >
                {r}
              </button>
            ))}
          </div>
          <button className="border border-[var(--lib-gold-text)] text-[var(--lib-gold-text)] hover:bg-[var(--lib-surface-2)] font-medium text-sm rounded-lg px-4 py-2 transition-colors flex items-center gap-2">
            <Download className="h-4 w-4" />
            Export
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="bg-[var(--lib-surface)] border border-[var(--lib-hairline)] rounded-lg">
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[var(--lib-hairline)]">
          {[
            { label: 'Active Loans', value: (overview.totalLoans).toLocaleString() },
            { label: 'Returns', value: (overview.totalReturns).toLocaleString() },
            { label: 'Fines Collected', value: `$${Number(overview.totalFinesCollected).toFixed(2)}` },
            { label: 'Avg Duration', value: `${overview.averageLoanDurationDays}d` },
          ].map(kpi => (
            <div key={kpi.label} className="px-6 py-4">
              <div className="font-display text-[28px] font-bold text-[var(--lib-ink)]">{kpi.value}</div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)] mt-1">{kpi.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Full-width loan activity area chart */}
      <div className="bg-[var(--lib-surface)] border border-[var(--lib-hairline)] rounded-lg p-5">
        <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)] mb-4">Loan Activity</div>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={loanTrends} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E8E4DC" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#9CA3AF' }} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: '#9CA3AF' }} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={CHART_STYLE} />
            <Area type="monotone" dataKey="loans" stroke={AMBER} fill="#FEF3C7" strokeWidth={2} name="Loans" />
            <Area type="monotone" dataKey="returns" stroke={GRAY} fill="#F9FAFB" strokeWidth={2} name="Returns" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Two-column charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Category bar chart */}
        <div className="bg-[var(--lib-surface)] border border-[var(--lib-hairline)] rounded-lg p-5">
          <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)] mb-4">By Category</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={categoryBreakdown} layout="vertical" margin={{ left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8E4DC" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10, fill: '#9CA3AF' }} tickLine={false} axisLine={false} />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fontSize: 11, fill: '#6B7280' }}
                tickLine={false}
                axisLine={false}
                width={80}
              />
              <Tooltip contentStyle={CHART_STYLE} />
              <Bar dataKey="count" fill={AMBER} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Member activity donut */}
        <div className="bg-[var(--lib-surface)] border border-[var(--lib-hairline)] rounded-lg p-5">
          <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)] mb-4">Member Activity</div>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={userTypeBreakdown}
                cx="50%"
                cy="45%"
                innerRadius={55}
                outerRadius={80}
                dataKey="value"
              >
                {userTypeBreakdown.map((_: any, i: number) => (
                  <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                ))}
              </Pie>
              <Legend iconSize={10} iconType="circle" wrapperStyle={{ fontSize: 11 }} />
              <Tooltip contentStyle={CHART_STYLE} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Two-column tables */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Popular Books */}
        <div className="bg-[var(--lib-surface)] border border-[var(--lib-hairline)] rounded-lg p-5">
          <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)] mb-4">Popular Books</div>
          <div className="space-y-3">
            {popularBooks.slice(0, 5).map((book: any, i: number) => {
              const maxCount = popularBooks[0]?.count ?? 1;
              const count = book.count ?? 0;
              return (
                <div key={book.id ?? i} className="flex items-start gap-2.5">
                  <span className="text-xs font-mono text-[var(--lib-gold-text)] font-bold w-5 flex-shrink-0 mt-0.5">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[var(--lib-ink)] truncate">
                      {book.title ?? book.book_title}
                    </div>
                    <div className="h-1 bg-[var(--lib-surface-2)] rounded-full mt-1.5">
                      <div
                        className="h-1 bg-[var(--lib-gold)] rounded-full"
                        style={{ width: `${maxCount > 0 ? (count / maxCount) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-xs text-[var(--lib-ink-muted)] font-mono flex-shrink-0">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Borrowers */}
        <div className="bg-[var(--lib-surface)] border border-[var(--lib-hairline)] rounded-lg p-5">
          <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)] mb-4">Top Borrowers</div>
          <div className="space-y-3">
            {topBorrowers.slice(0, 5).map((u: any, i: number) => (
              <div key={u.id ?? i} className="flex items-center gap-3">
                <span className="text-xs font-mono text-[var(--lib-gold-text)] font-bold w-5 flex-shrink-0">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div className="w-7 h-7 rounded-full bg-[var(--lib-gold)]/10 text-[var(--lib-gold-text)] flex items-center justify-center text-xs font-bold flex-shrink-0">
                  {(u.full_name ?? u.username ?? 'U').substring(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[var(--lib-ink)] truncate">
                    {u.full_name ?? u.username}
                  </div>
                  <div className="text-xs text-[var(--lib-ink-muted)] capitalize">{u.role ?? u.user_type}</div>
                </div>
                <span className="text-xs text-[var(--lib-ink-muted)] font-mono flex-shrink-0">
                  {u.loan_count ?? u.total_loans ?? 0}
                </span>
              </div>
            ))}
            {topBorrowers.length === 0 && (
              <div className="text-sm text-[var(--lib-ink-muted)] text-center py-6">No borrower data available.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsModule;
